import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { VehicleLog } from '../../../schema/Log.ts';
import { IVehicle } from '../../../schema/Vehicle.ts';
import { useVehicleSystemLogs } from '../../../services/Logs.ts';
import { useTerritoryConfig } from '../../../services/Territory.ts';
import { FormatTime, getLogLevel } from '../../../utils/utils.ts';

export function VehicleSystemLogs(props: Readonly<{ vehicle: IVehicle }>) {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const { data: territoryConfig } = useTerritoryConfig(params.territorySlug, props.vehicle.id);
  const {
    data: VehicleSystemLogsData,
    isLoading,
    error,
  } = useVehicleSystemLogs(params.territorySlug, props.vehicle.id, territoryConfig);
  const VehicleSystemTableColumns = [
    {
      title: t('logs.table-time'),
      dataIndex: 'start_datetime',
      key: 'start_datetime',
      render: (text: string) => <p className={'text-basic'}>{FormatTime(text, true)}</p>,
      width: '10%',
    },
    {
      title: t('logs.table-level'),
      dataIndex: 'level',
      key: 'level',
      render: (level: string) => (
        <span className={'custom-badge rounded-4'} style={getLogLevel(level)}>
          {getLogLevel(level).name}
        </span>
      ),
      width: '10%',
      filters: [
        {
          text: 'Debug',
          value: 'debug',
        },
        {
          text: 'Info',
          value: 'info',
        },
        {
          text: 'Warning',
          value: 'warning',
        },
        {
          text: 'Error',
          value: 'error',
        },
        {
          text: 'Critical',
          value: 'critical',
        },
      ],
      onFilter: (value: string, record: VehicleLog) => record.level === value,
    },
    {
      title: t('logs.table-code'),
      dataIndex: 'code',
      key: 'code',
      width: '10%',
      render: (code: string) => <p className={'text-basic'}>{code}</p>,
    },
    {
      title: t('logs.table-category'),
      dataIndex: 'category',
      key: 'category',
      width: '10%',
      render: (category: string) => <p className={'text-basic'}>{category}</p>,
    },
    {
      title: t('logs.table-context'),
      dataIndex: 'context',
      key: 'context',
      width: '60%',
      render: (context: { [key: string]: string }) => (
        <ul className={'m-0 p-0 ms-2'}>
          {Object.keys(context).map((key: string) => (
            <li className={'m-0 p-0'} key={key}>
              <p className={'text-basic'}>
                {key} : {JSON.stringify(context[key])}
              </p>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return error ? (
    <p className={'text-basic text-center'}>{t('logs.error')}</p>
  ) : (
    <Table
      rowKey={record => `${record.start_datetime}-${record.description}`}
      //eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      columns={VehicleSystemTableColumns}
      pagination={false}
      loading={isLoading}
      scroll={{ y: 'max-content' }}
      dataSource={VehicleSystemLogsData}
    />
  );
}
